import { Api, IConfig } from '@estee/elc-service';
import { POWER_REVIEWS_API_LINK } from '../internal/constants/PowerReviews';
import { IlocaleDependentIds } from '~interfaces/IRating';
import { Config } from '~domain/entities/Config';

enum ApiResource {
    Snippet = 'snippet',
    Reviews = 'reviews'
}

interface IResultData<T> {
    data: {
        results: T[];
    };
}

export interface IRatingSnippetsApiResult {
    page_id: string;
    rollup: IRollupJsonData;
}

export interface IReviewsApiResult {
    page_id: string;
    reviews: IReviewJsonData[];
}

export interface IReviewJsonData {
    details: IReviewDetailsJsonData;
    metrics: IReviewMetricsJsonData;
}

interface IReviewMetricsJsonData {
    rating: number;
}

interface IReviewDetailsJsonData {
    headline: string;
}

interface IRollupJsonData {
    average_rating: number;
    review_count: number;
    answered_questions: number;
}

export class RatingsAndReviewsApiSdk extends Api {
    private configEntity: Config;

    constructor(
        config: IConfig,
        serviceName: string,
        serviceVersion: string,
        configEntity: Config
    ) {
        super(config, serviceName, serviceVersion);
        this.configEntity = configEntity;
    }

    private buildUrl(pageIds: string[], resource: ApiResource) {
        const { ratingsReviewsConfig, i18n } = this.configEntity.config;
        const { merchantId, locale, snippet_locale, apiKey, localeDependentIds } =
            ratingsReviewsConfig;
        const joinedPageIds = pageIds.join(',');
        let locale_used = locale;
        if (resource === ApiResource.Snippet) {
            // For some site e.g. CL JP, the locale comments may too few or empty
            // we want to get the comments count for all locales
            // and the snippet_locale is the new introduced config to used for get snippet data only
            // which is optional and if not set will fallback to default locale
            locale_used = snippet_locale ?? locale;
        }
        const localeDependentIdsFiltered = localeDependentIds?.filter(
            (localeDependentId: IlocaleDependentIds) => localeDependentId.locale === i18n.locale
        );
        if (localeDependentIdsFiltered?.length > 0) {
            locale_used = localeDependentIdsFiltered[0].power_reviews_locale;
        }
        return `${POWER_REVIEWS_API_LINK}/m/${merchantId}/l/${locale_used}/product/${joinedPageIds}/${resource}?apikey=${apiKey}`;
    }

    public async getRatingSnippetsData(pageIds: string[]): Promise<IRatingSnippetsApiResult[]> {
        try {
            const url = this.buildUrl(pageIds, ApiResource.Snippet);
            const response: IResultData<IRatingSnippetsApiResult> = await this.httpTransport.get({
                url
            });

            return response.data.results;
        } catch (err) {
            throw err;
        }
    }

    public async getReviewsData(pageId: string): Promise<IReviewsApiResult[]> {
        try {
            const url = this.buildUrl([pageId], ApiResource.Reviews);
            const response: IResultData<IReviewsApiResult> = await this.httpTransport.get({ url });

            return response.data.results;
        } catch (err) {
            throw err;
        }
    }
}
